import { useState } from 'react';
import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Select, Space, Tag, Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { t } from '../../i18n';

interface Ticket {
  id: number;
  subject: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  createdAt: string;
  user: {
    fullName: string;
    phone: string;
  };
}

export const SupportTicketList = () => {
  const { show } = useNavigation();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<Ticket>({
    resource: 'admin/tickets',
    syncWithLocation: true,
    filters: {
      permanent: [
        { field: 'isEmergency', operator: 'eq', value: false },
        { field: 'isSuggestion', operator: 'eq', value: false },
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
      ],
    },
  });

  const statusLabels: Record<string, string> = {
    OPEN: t('support.statusOpen'),
    IN_PROGRESS: t('support.statusInProgress'),
    RESOLVED: t('support.statusResolved'),
    CLOSED: t('support.statusClosed'),
  };

  const statusColors: Record<string, string> = {
    OPEN: 'red',
    IN_PROGRESS: 'orange',
    RESOLVED: 'green',
    CLOSED: 'default',
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('support.ticketSubject'),
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: t('support.ticketUser'),
      dataIndex: 'user',
      key: 'user',
      render: (user: Ticket['user']) => (
        <div>
          <div>{user?.fullName || t('common.noName')}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{user?.phone}</div>
        </div>
      ),
    },
    {
      title: t('support.ticketStatus'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: t('support.ticketDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: Ticket) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('support', record.id)}
        />
      ),
    },
  ];

  return (
    <List
      title={t('support.title')}
      headerButtons={
        <Space>
          <Select
            placeholder={t('common.status')}
            allowClear
            style={{ width: 140 }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            options={Object.entries(statusLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
        </Space>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
